/**
 * "New campaign" as a CRM dialog.
 *
 * Was an inline form that pushed the list down when it opened, which is the
 * pre-redesign habit this pass removes: the list is the page, and a form that
 * shoves it out of the way is a dialog that forgot to be one.
 *
 * The slug is shown as it is derived rather than left for the operator to find
 * out about later - it is the campaign's public identity and it cannot be
 * changed afterwards.
 *
 * SEEDED OR EMPTY. Opened from the campaign list it starts with no audience and
 * the operator picks a source. Opened from the register list it arrives with
 * that page's current filter already in hand, which is the whole point of the
 * button over there - the alternative is rebuilding a twenty-field filter from
 * memory in a different screen, and getting it slightly wrong.
 *
 * ⚠ A SEEDED SOURCE IS NOT A CHOICE. The filter belongs to the screen it came
 * from, so the picker is replaced by a line naming it. A register filter stored
 * under `scanLeads` would not fail - the server would reject it, or worse, a
 * `.strict()` schema would take an empty object and the campaign would select
 * every lead there is.
 */
import { useState } from "react";
import { LocaleCombobox } from "@wa/ui";
import { Loader2, Plus } from "lucide-react";
import { useI18n, useT } from "../../../i18n/I18nProvider";
import { createCampaign } from "../../../lib/marketing";
import { Btn, Modal, SelectInput } from "../../ui";
import { slugify } from "./lib";
import { num } from "./shared";

type Source = "register" | "scanLeads";

/** A filter carried in from the screen the operator was looking at. */
export interface CampaignSeed {
  source: Source;
  /** Already pruned to what a strict server schema takes. */
  filter: Record<string, unknown>;
  /**
   * How many rows that screen was showing. COMPANIES, not mailable addresses -
   * the two differ by every company with no address on file, by the dedup and
   * by the suppression list, and the campaign's own preview is what answers the
   * second question. Shown so the operator can see what they picked up, never
   * saved as the audience count.
   */
  count: number | null;
  /** The active filters in words, as the source screen already renders them. */
  chips: string[];
}

/** The languages a campaign's letter is written in. */
const CAMPAIGN_LOCALES = ["et", "en"] as const;

export function NewCampaignModal({
  onClose,
  onCreated,
  onError,
  seed,
}: {
  onClose: () => void;
  /** Handed the new campaign's id, so the caller can route straight into it. */
  onCreated: (id: string) => void;
  onError: () => void;
  seed?: CampaignSeed;
}) {
  const t = useT();
  const { locale: uiLocale } = useI18n();
  const [name, setName] = useState("");
  const [locale, setLocale] = useState("et");
  const [source, setSource] = useState<Source>(seed?.source ?? "register");
  const [busy, setBusy] = useState(false);

  const trimmed = name.trim();
  const slug = slugify(trimmed);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!trimmed) return;
    setBusy(true);
    try {
      const res = await createCampaign({
        name: trimmed,
        // A name made entirely of characters the slug drops (a name in Cyrillic,
        // or just punctuation) would otherwise post an empty slug and 400.
        slug: slug || `campaign-${Date.now().toString(36)}`,
        locale,
        audience: { source, filter: seed?.filter ?? {} },
      });
      onCreated(res.campaign.id);
    } catch {
      onError();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={t("marketing.new")} label={t("marketing.new")} onClose={onClose} className="max-w-[460px]">
      <form onSubmit={submit} className="mt-1 flex flex-col gap-3">
        <div>
          <span className="crm-lab">{t("marketing.col.name")} *</span>
          <label className="crm-inp">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("marketing.namePlaceholder")}
              autoComplete="off"
              autoFocus
              required
            />
          </label>
          {slug ? <p className="crm-fnote crm-tnum mt-1">/{slug}</p> : null}
        </div>

        <div>
          <span className="crm-lab">{t("marketing.col.audience")}</span>
          {seed ? (
            <>
              {/* Two sentences, because "with the filter you have on now" is a
                  lie when there is no filter - and that case is the dangerous
                  one, so it must not be described in the words of the safe one. */}
              <p className="crm-sub">
                {t(seed.chips.length > 0 ? "marketing.seed.from" : "marketing.seed.all", {
                  source: t(`marketing.source.${seed.source}`),
                  n: seed.count === null ? "?" : num(seed.count),
                })}
              </p>
              {seed.chips.length > 0 ? (
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {seed.chips.map((c) => (
                    <span key={c} className="crm-chip">
                      {c}
                    </span>
                  ))}
                </div>
              ) : (
                // No filter at all is a real and dangerous choice - it selects
                // the whole register - so it is said out loud rather than shown
                // as an empty row of chips.
                <p className="crm-fnote mt-1">{t("marketing.seed.noFilter")}</p>
              )}
              <p className="crm-fnote mt-1.5">{t("marketing.seed.note")}</p>
            </>
          ) : (
            <SelectInput
              value={source}
              onChange={(next) => setSource(next as Source)}
              label={t("marketing.col.audience")}
            >
              <option value="register">{t("marketing.source.register")}</option>
              <option value="scanLeads">{t("marketing.source.scanLeads")}</option>
            </SelectInput>
          )}
        </div>

        <div>
          <span className="crm-lab">{t("marketing.language")}</span>
          <LocaleCombobox
            className="h-[52px] border-0 px-[18px] text-[13.5px] shadow-none"
            popoverClassName="rounded-2xl"
            locales={CAMPAIGN_LOCALES}
            displayLocale={uiLocale}
            value={locale}
            onChange={setLocale}
            aria-label={t("marketing.language")}
            searchPlaceholder={t("common.localeSearch")}
            emptyLabel={t("common.localeNone")}
          />
        </div>

        <div className="crm-m-foot">
          <Btn type="button" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </Btn>
          <Btn type="submit" tone="brand" disabled={busy || !trimmed}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
            {t("marketing.create")}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}
